import type { ReactNode } from "react";
import { Box, Breadcrumbs, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { ChevronRightIcon } from "@/design/icons/ChevronRightIcon";

interface Crumb {
  label: string;
  to?: string;
}

/**
 * The frame every screen sits in.
 *
 * Title, an optional one-line explanation, breadcrumbs and the actions for this
 * page. Having it in one place is what stops fifteen screens each inventing
 * their own heading spacing.
 */
export default function Page({
  title, subtitle, crumbs, actions, fill, children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  crumbs?: Crumb[];
  actions?: ReactNode;
  /**
   * Fit the page to the window on desktop: the shell stops scrolling and the
   * page's children get whatever height is left (they should scroll inside).
   */
  fill?: boolean;
  children: ReactNode;
}) {
  return (
    <Box {...(fill ? { "data-fill-page": "" } : {})}
         sx={fill ? { display: { md: "flex" }, flexDirection: "column", flex: 1, minHeight: 0 } : undefined}>
      {crumbs?.length ? (
        <Breadcrumbs sx={{ mb: 0.75, color: "text.secondary" }} separator={<ChevronRightIcon sx={{ fontSize: 12 }} />}>
          {crumbs.map((crumb) =>
            crumb.to ? (
              <Typography
                key={crumb.label} component={RouterLink} to={crumb.to}
                variant="caption" color="text.secondary"
                sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                {crumb.label}
              </Typography>
            ) : (
              <Typography key={crumb.label} variant="caption" color="text.primary">
                {crumb.label}
              </Typography>
            ),
          )}
        </Breadcrumbs>
      ) : null}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          {/* MultiDoc design system: the page title is the page's h1, 24/600. */}
          <Typography variant="h3" component="h1" sx={{ wordBreak: "break-word" }}>
            {title}
          </Typography>
          {subtitle ? (
            // A div, not a p: subtitles carry status chips and other block
            // elements, and a div inside a p is invalid HTML.
            <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {actions ? <Stack direction="row" spacing={1} flexShrink={0} alignItems="center">{actions}</Stack> : null}
      </Stack>

      {children}
    </Box>
  );
}
